import type { AxiosError, AxiosInstance } from 'axios';
import { errorBus } from '@/api/errorBus';
import { extractErrorMessage, type ApiErrorPayload } from '@/api/extractErrorMessage';

/**
 * Attaches a global response error interceptor to the given Axios instance.
 *
 * Behaviour:
 *  - 401 → emits `unauthorized` on the errorBus (AuthGuard shows the
 *           "Session Expired" modal and redirects to login).
 *  - Other errors → emits `api-error` on the errorBus with the message
 *                   and status code (ApiErrorModal displays it).
 */
export function attachErrorInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiErrorPayload>) => {
      const status = error.response?.status;

      if (status === 401) {
        localStorage.removeItem('TOKEN');
        localStorage.removeItem('user_details_from_server');
        errorBus.emit('unauthorized');
        return Promise.reject(error);
      }

      const serverMessage = extractErrorMessage(
        error.response?.data,
        error.message || 'An unexpected error occurred.'
      );

      errorBus.emit('api-error', { message: serverMessage, status });

      return Promise.reject(error);
    }
  );
}
